import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
  Table
} from 'react-bootstrap';
import rendezVousService from '../../../services/rendezVousService';
import receptionService from '../../../services/receptionService';
import externalMemberService from '../../../services/externalMemberService';
import { useDoctorWorkspace } from '../doctorWorkspaceContext';
import {
  formatDateTimeForDb,
  toDateTimeLocalValue,
  todayYmd,
  parseToInstant,
  formatAppDateTime,
  formatAppTime
} from '../../../utils/appTimezone';

const STATUS_VARIANT = {
  EN_ATTENTE: 'warning',
  CONFIRME: 'primary',
  TERMINE: 'success',
  ANNULE: 'danger',
  ABSENT: 'secondary'
};

const STATUS_LABEL = {
  EN_ATTENTE: 'En attente',
  CONFIRME: 'Confirmé',
  TERMINE: 'Terminé',
  ANNULE: 'Annulé',
  ABSENT: 'Absent'
};

const emptyForm = {
  doctor_id: '',
  start_at: '',
  end_at: '',
  motif: '',
  notes: '',
  patient_type: 'SMIE',
  patient_id: ''
};

const formatPatientLabel = (item) => {
  if (item.member_id) {
    const name = `${item.member_nom || ''} ${item.member_prenom || ''}`.trim();
    if (item.member_type && item.member_type !== 'AGENT') return `${name} (ayant droit)`;
    return name || item.member_matricule || `Membre #${item.member_id}`;
  }
  const extName = `${item.external_nom || ''} ${item.external_prenom || ''}`.trim();
  return extName || `Externe #${item.external_member_id}`;
};

export default function DoctorAppointmentsPage() {
  const { toast } = useDoctorWorkspace();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [items, setItems] = useState([]);
  const [calendarRows, setCalendarRows] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [patientSearch, setPatientSearch] = useState('');
  const [patientOptions, setPatientOptions] = useState([]);
  const [patientSearchLoading, setPatientSearchLoading] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    doctor_id: '',
    date_from: '',
    date_to: '',
    search: ''
  });
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmReason, setConfirmReason] = useState('');

  const loadDoctors = useCallback(async () => {
    const res = await rendezVousService.doctors();
    if (res.success) {
      const list = Array.isArray(res.data) ? res.data : [];
      setDoctors(list);
      setForm((prev) => ({
        ...prev,
        doctor_id: prev.doctor_id || (list[0] ? String(list[0].id) : '')
      }));
    }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await rendezVousService.list({
        ...filters,
        limit: 100,
        offset: 0
      });
      if (res.success) {
        setItems(Array.isArray(res.data) ? res.data : []);
      }
    } catch (e) {
      toast('danger', e.message || 'Chargement rendez-vous impossible');
    } finally {
      setLoading(false);
    }
  }, [filters, toast]);

  const loadCalendar = useCallback(async () => {
    try {
      const res = await rendezVousService.calendar({
        doctor_id: filters.doctor_id || undefined,
        date_from: filters.date_from || undefined,
        date_to: filters.date_to || undefined
      });
      if (res.success) {
        setCalendarRows(Array.isArray(res.data) ? res.data : []);
      }
    } catch (e) {
      toast('warning', e.message || 'Calendrier indisponible');
    }
  }, [filters.date_from, filters.date_to, filters.doctor_id, toast]);

  useEffect(() => {
    loadDoctors();
  }, [loadDoctors]);

  useEffect(() => {
    loadList();
    loadCalendar();
  }, [loadList, loadCalendar]);

  useEffect(() => {
    const shouldCheck = form.doctor_id && form.start_at && form.end_at;
    if (!shouldCheck) {
      setAvailability(null);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await rendezVousService.availability({
          doctor_id: form.doctor_id,
          start_at: formatDateTimeForDb(form.start_at),
          end_at: formatDateTimeForDb(form.end_at),
          ...(editingId ? { exclude_id: editingId } : {})
        });
        if (res.success) setAvailability(res.data || null);
      } catch (e) {
        setAvailability({ available: false, conflicts: [], message: e.message });
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [form.doctor_id, form.start_at, form.end_at, editingId]);

  useEffect(() => {
    const q = patientSearch.trim();
    if (q.length < 2) {
      setPatientOptions([]);
      return;
    }
    const timer = setTimeout(async () => {
      setPatientSearchLoading(true);
      try {
        if (form.patient_type === 'SMIE') {
          const res = await receptionService.searchMembers(q, { limit: 15 });
          const rows = (res?.data || []).map((m) => ({
            id: m.id,
            label: `${m.nom || ''} ${m.prenom || ''}`.trim() + (m.type !== 'AGENT' ? ' (ayant droit)' : '')
          }));
          setPatientOptions(rows);
        } else {
          const res = await externalMemberService.list({ search: q, limit: 15, offset: 0 });
          const rows = (res?.data || []).map((m) => ({
            id: m.id,
            label: `${m.nom || ''} ${m.prenom || ''}`.trim()
          }));
          setPatientOptions(rows);
        }
      } catch (e) {
        setPatientOptions([]);
      } finally {
        setPatientSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [patientSearch, form.patient_type]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        type_rendez_vous: 'MEDECIN',
        service: 'CONSULTATION_MEDECIN',
        priority: 'NORMALE',
        doctor_preference: 'SPECIFIQUE',
        doctor_id: Number(form.doctor_id),
        start_at: formatDateTimeForDb(form.start_at),
        end_at: formatDateTimeForDb(form.end_at),
        motif: form.motif.trim(),
        notes: form.notes.trim() || null,
        ...(form.patient_type === 'SMIE'
          ? { member_id: Number(form.patient_id) }
          : { external_member_id: Number(form.patient_id) })
      };
      if (editingId) {
        await rendezVousService.update(editingId, payload);
        toast('success', 'Rendez-vous mis à jour');
      } else {
        await rendezVousService.create(payload);
        toast('success', 'Rendez-vous créé');
      }
      setForm((prev) => ({ ...emptyForm, doctor_id: prev.doctor_id }));
      setPatientOptions([]);
      setPatientSearch('');
      setEditingId(null);
      await loadList();
      await loadCalendar();
    } catch (e2) {
      toast('danger', e2.message || 'Enregistrement impossible');
    } finally {
      setSaving(false);
    }
  };

  const openDetail = async (id) => {
    setDetailLoading(true);
    setDetail(null);
    try {
      const res = await rendezVousService.getById(id);
      if (res.success) setDetail(res.data || null);
    } catch (e) {
      toast('danger', e.message || 'Détail indisponible');
    } finally {
      setDetailLoading(false);
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setForm({
      doctor_id: String(item.doctor_id || ''),
      start_at: toDateTimeLocalValue(item.start_at),
      end_at: toDateTimeLocalValue(item.end_at),
      motif: item.motif || '',
      notes: item.notes || '',
      patient_type: item.member_id ? 'SMIE' : 'EXTERNE',
      patient_id: String(item.member_id || item.external_member_id || '')
    });
    setPatientSearch(formatPatientLabel(item));
  };

  const requestStatusAction = (type, item) => {
    setConfirmAction({ type, item });
    setConfirmReason('');
  };

  const executeStatusAction = async () => {
    if (!confirmAction?.item?.id) return;
    const { type, item } = confirmAction;
    try {
      if (type === 'confirm') await rendezVousService.confirm(item.id);
      if (type === 'finish') await rendezVousService.finish(item.id);
      if (type === 'cancel') await rendezVousService.cancel(item.id, confirmReason.trim());
      if (type === 'absent') await rendezVousService.absent(item.id);
      toast('success', 'Statut du rendez-vous mis à jour');
      setConfirmAction(null);
      setConfirmReason('');
      await loadList();
      await loadCalendar();
      if (detail?.id === item.id) await openDetail(item.id);
    } catch (e) {
      toast('danger', e.message || 'Transition impossible');
    }
  };

  const groupedCalendar = useMemo(() => {
    const groups = new Map();
    calendarRows.forEach((row) => {
      const key = todayYmd(parseToInstant(row.start_at));
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(row);
    });
    return [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [calendarRows]);

  return (
    <div className="doctor-dashboard recv-dash-pro--main">
      <Card className="shadow-sm border-0 mb-3">
        <Card.Body>
          <h4 className="mb-1">Gestion des rendez-vous</h4>
          <p className="text-muted mb-0 small">
            Calendrier, disponibilités, transitions de statut et suivi des actions.
          </p>
        </Card.Body>
      </Card>

      <Card className="shadow-sm border-0 mb-3">
        <Card.Body>
          <Form onSubmit={submit}>
            <Row className="g-3">
              <Col md={3}>
                <Form.Label>Médecin</Form.Label>
                <Form.Select
                  value={form.doctor_id}
                  onChange={(e) => setForm((prev) => ({ ...prev, doctor_id: e.target.value }))}
                  required
                >
                  <option value="">Sélectionner…</option>
                  {doctors.map((d) => (
                    <option key={d.id} value={d.id}>
                      {`${d.first_name || ''} ${d.last_name || ''}`.trim()} ({d.role})
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Label>Début</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={form.start_at}
                  onChange={(e) => setForm((prev) => ({ ...prev, start_at: e.target.value }))}
                  required
                />
              </Col>
              <Col md={3}>
                <Form.Label>Fin</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={form.end_at}
                  onChange={(e) => setForm((prev) => ({ ...prev, end_at: e.target.value }))}
                  required
                />
              </Col>
              <Col md={3}>
                <Form.Label>Type patient</Form.Label>
                <Form.Select
                  value={form.patient_type}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      patient_type: e.target.value,
                      patient_id: ''
                    }))
                  }
                >
                  <option value="SMIE">SMIE / ayant droit</option>
                  <option value="EXTERNE">Membre externe</option>
                </Form.Select>
              </Col>
              <Col md={6}>
                <Form.Label>Recherche patient</Form.Label>
                <Form.Control
                  type="search"
                  placeholder="Nom, prénom, matricule…"
                  value={patientSearch}
                  onChange={(e) => {
                    setPatientSearch(e.target.value);
                    setForm((prev) => ({ ...prev, patient_id: '' }));
                  }}
                />
                {patientSearchLoading && (
                  <div className="small text-muted mt-1">
                    <Spinner animation="border" size="sm" className="me-1" />
                    Recherche en cours...
                  </div>
                )}
                {!patientSearchLoading && patientOptions.length > 0 && (
                  <div className="border rounded mt-1 bg-white">
                    {patientOptions.map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        className="btn btn-link text-start w-100 text-decoration-none py-1 px-2"
                        onClick={() => {
                          setForm((prev) => ({ ...prev, patient_id: String(opt.id) }));
                          setPatientSearch(opt.label);
                          setPatientOptions([]);
                        }}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </Col>
              <Col md={6}>
                <Form.Label>Motif</Form.Label>
                <Form.Control
                  value={form.motif}
                  onChange={(e) => setForm((prev) => ({ ...prev, motif: e.target.value }))}
                  required
                />
              </Col>
              <Col md={9}>
                <Form.Label>Notes (optionnel)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={form.notes}
                  onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
                />
              </Col>
              <Col md={3} className="d-grid gap-2 align-content-end">
                <Button type="submit" disabled={saving || !form.patient_id}>
                  {saving ? 'Enregistrement…' : editingId ? 'Mettre à jour' : 'Créer rendez-vous'}
                </Button>
                {editingId && (
                  <Button
                    variant="outline-secondary"
                    onClick={() => {
                      setEditingId(null);
                      setForm((prev) => ({ ...emptyForm, doctor_id: prev.doctor_id }));
                      setPatientSearch('');
                      setPatientOptions([]);
                    }}
                  >
                    Annuler édition
                  </Button>
                )}
              </Col>
            </Row>
          </Form>
          {availability && (
            <Alert variant={availability.available ? 'success' : 'warning'} className="mt-3 mb-0">
              {availability.available
                ? 'Créneau disponible pour ce médecin.'
                : 'Créneau indisponible: chevauchement détecté.'}
            </Alert>
          )}
        </Card.Body>
      </Card>

      <Card className="shadow-sm border-0 mb-3">
        <Card.Header className="bg-white">
          <strong>Filtres avancés</strong>
        </Card.Header>
        <Card.Body>
          <Row className="g-2">
            <Col md={3}>
              <Form.Control
                type="search"
                placeholder="Recherche ref/motif/patient"
                value={filters.search}
                onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
              />
            </Col>
            <Col md={2}>
              <Form.Select
                value={filters.status}
                onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
              >
                <option value="">Tous statuts</option>
                {Object.keys(STATUS_LABEL).map((s) => (
                  <option key={s} value={s}>
                    {STATUS_LABEL[s]}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Select
                value={filters.doctor_id}
                onChange={(e) => setFilters((prev) => ({ ...prev, doctor_id: e.target.value }))}
              >
                <option value="">Tous médecins</option>
                {doctors.map((d) => (
                  <option key={d.id} value={d.id}>
                    {`${d.first_name || ''} ${d.last_name || ''}`.trim()}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Control
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters((prev) => ({ ...prev, date_from: e.target.value }))}
              />
            </Col>
            <Col md={2}>
              <Form.Control
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters((prev) => ({ ...prev, date_to: e.target.value }))}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="shadow-sm border-0 mb-3">
        <Card.Header className="bg-white">
          <strong>Liste des rendez-vous</strong>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          ) : items.length === 0 ? (
            <Alert variant="info" className="mb-0">
              Aucun rendez-vous trouvé.
            </Alert>
          ) : (
            <div className="table-responsive">
              <Table hover>
                <thead>
                  <tr>
                    <th>Référence</th>
                    <th>Patient</th>
                    <th>Médecin</th>
                    <th>Créneau</th>
                    <th>Statut</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id}>
                      <td className="fw-semibold">{item.reference}</td>
                      <td>{formatPatientLabel(item)}</td>
                      <td>{`${item.doctor_first_name || ''} ${item.doctor_last_name || ''}`.trim()}</td>
                      <td>
                        {formatAppDateTime(item.start_at)} – {formatAppTime(item.end_at)}
                      </td>
                      <td>
                        <Badge bg={STATUS_VARIANT[item.status] || 'secondary'}>
                          {STATUS_LABEL[item.status] || item.status}
                        </Badge>
                      </td>
                      <td>
                        <div className="d-flex flex-wrap gap-1">
                          <Button size="sm" variant="outline-primary" onClick={() => openDetail(item.id)}>
                            Détail
                          </Button>
                          {(item.status === 'EN_ATTENTE' || item.status === 'CONFIRME') && (
                            <Button size="sm" variant="outline-secondary" onClick={() => startEdit(item)}>
                              Modifier
                            </Button>
                          )}
                          {item.status === 'EN_ATTENTE' && (
                            <Button
                              size="sm"
                              variant="outline-success"
                              onClick={() => requestStatusAction('confirm', item)}
                            >
                              Confirmer
                            </Button>
                          )}
                          {item.status === 'CONFIRME' && (
                            <Button
                              size="sm"
                              variant="outline-success"
                              onClick={() => requestStatusAction('finish', item)}
                            >
                              Terminer
                            </Button>
                          )}
                          {(item.status === 'EN_ATTENTE' || item.status === 'CONFIRME') && (
                            <>
                              <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={() => requestStatusAction('cancel', item)}
                              >
                                Annuler
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-dark"
                                onClick={() => requestStatusAction('absent', item)}
                              >
                                Absent
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      <Card className="shadow-sm border-0">
        <Card.Header className="bg-white">
          <strong>Calendrier des rendez-vous</strong>
        </Card.Header>
        <Card.Body>
          {groupedCalendar.length === 0 ? (
            <Alert variant="light" className="mb-0">
              Aucun rendez-vous dans la plage sélectionnée.
            </Alert>
          ) : (
            groupedCalendar.map(([day, list]) => (
              <div key={day} className="mb-3">
                <div className="fw-semibold mb-2">{new Date(day).toLocaleDateString()}</div>
                <div className="d-flex flex-wrap gap-2">
                  {list.map((row) => (
                    <Card key={row.id} className="border" style={{ minWidth: 260 }}>
                      <Card.Body className="py-2">
                        <div className="small text-muted">{row.reference}</div>
                        <div className="fw-semibold">
                          {new Date(row.start_at).toLocaleTimeString()} -{' '}
                          {new Date(row.end_at).toLocaleTimeString()}
                        </div>
                        <div className="small">{formatPatientLabel(row)}</div>
                        <Badge bg={STATUS_VARIANT[row.status] || 'secondary'}>
                          {STATUS_LABEL[row.status] || row.status}
                        </Badge>
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              </div>
            ))
          )}
        </Card.Body>
      </Card>

      <Modal show={!!detail || detailLoading} onHide={() => setDetail(null)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Détail rendez-vous</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detailLoading ? (
            <div className="text-center py-3">
              <Spinner animation="border" />
            </div>
          ) : !detail ? (
            <Alert variant="warning" className="mb-0">
              Détail indisponible.
            </Alert>
          ) : (
            <>
              <p className="mb-1">
                <strong>Référence:</strong> {detail.reference}
              </p>
              <p className="mb-1">
                <strong>Patient:</strong> {formatPatientLabel(detail)}
              </p>
              <p className="mb-1">
                <strong>Médecin:</strong>{' '}
                {`${detail.doctor_first_name || ''} ${detail.doctor_last_name || ''}`.trim()}
              </p>
              <p className="mb-1">
                <strong>Motif:</strong> {detail.motif}
              </p>
              <p className="mb-2">
                <strong>Statut:</strong>{' '}
                <Badge bg={STATUS_VARIANT[detail.status] || 'secondary'}>
                  {STATUS_LABEL[detail.status] || detail.status}
                </Badge>
              </p>
              <hr />
              <h6>Historique</h6>
              {Array.isArray(detail.history) && detail.history.length > 0 ? (
                <ul className="small mb-0">
                  {detail.history.map((h) => (
                    <li key={h.id}>
                      {formatAppDateTime(h.created_at)} - {h.action}
                      {h.old_status || h.new_status
                        ? ` (${h.old_status || '-'} -> ${h.new_status || '-'})`
                        : ''}
                      {h.note ? ` - ${h.note}` : ''}
                    </li>
                  ))}
                </ul>
              ) : (
                <Alert variant="light" className="mb-0">
                  Aucun événement d&apos;historique.
                </Alert>
              )}
            </>
          )}
        </Modal.Body>
      </Modal>

      <Modal show={!!confirmAction} onHide={() => setConfirmAction(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirmation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {confirmAction && (
            <>
              <p className="mb-2">
                Action <strong>{confirmAction.type}</strong> sur le rendez-vous{' '}
                <strong>{confirmAction.item.reference}</strong>.
              </p>
              {confirmAction.type === 'cancel' && (
                <Form.Group>
                  <Form.Label>Motif d&apos;annulation</Form.Label>
                  <Form.Control
                    value={confirmReason}
                    onChange={(e) => setConfirmReason(e.target.value)}
                    required
                  />
                </Form.Group>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setConfirmAction(null)}>
            Fermer
          </Button>
          <Button
            variant="primary"
            onClick={executeStatusAction}
            disabled={confirmAction?.type === 'cancel' && !confirmReason.trim()}
          >
            Valider
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
